import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Alert,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { useAuth } from '../../../contexts/AuthContext.js';

const ClaimAdmin = ({ setError: setParentError, setSuccess: setParentSuccess }) => {
  const { currentUser } = useAuth();
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState('uid');
  const [userData, setUserData] = useState(null);
  const [userClaims, setUserClaims] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localSuccess, setLocalSuccess] = useState(null);

  // Helper function to set errors both locally and in parent
  const setError = (message) => {
    setLocalError(message);
    if (setParentError) setParentError(message);
  };

  // Helper function to set success messages both locally and in parent
  const setSuccess = (message) => {
    setLocalSuccess(message);
    if (setParentSuccess) setParentSuccess(message);
  };

  // Clear messages after 5 seconds
  const clearMessages = () => {
    setTimeout(() => {
      setLocalError(null);
      setLocalSuccess(null);
    }, 5000);
  };

  const handleSearchTypeChange = (event, newType) => {
    if (newType !== null) {
      setSearchType(newType);
    }
  };

  const fetchUser = async () => {
    if (!searchValue.trim()) {
      setError(`Please enter a user ${searchType === 'uid' ? 'ID' : 'email'}`);
      clearMessages();
      return;
    }

    setLoading(true);
    setLocalError(null);
    setLocalSuccess(null);
    setUserData(null);
    setUserClaims(null);

    try {
      const token = await currentUser.getIdToken();
      
      let uid = searchValue;
      
      // If searching by email, we need to first find the user's UID
      if (searchType === 'email') {
        const emailSearchResponse = await fetch('/api/auth/getUserByEmail', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ email: searchValue })
        });
        
        if (!emailSearchResponse.ok) {
          const errorData = await emailSearchResponse.json();
          throw new Error(errorData.error || 'User not found with that email');
        }
        
        const emailSearchData = await emailSearchResponse.json();
        uid = emailSearchData.uid;
      }
      
      // Now that we have the UID (either directly or from email search), get user data
      const response = await fetch('/api/admin/user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ uid })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'User not found');
      }
      
      const userData = await response.json();
      setUserData(userData);
      
      // Get user claims
      const claimsResponse = await fetch('/api/auth/getUserClaims', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ uid: userData.uid })
      });
      
      if (!claimsResponse.ok) {
        const errorData = await claimsResponse.json();
        throw new Error(errorData.error || 'Failed to fetch user claims');
      }
      
      const claimsData = await claimsResponse.json();
      setUserClaims(claimsData.customClaims || {});
      
      setSuccess(`User found: ${userData.email || userData.uid}`);
      clearMessages();
    } catch (err) {
      console.error('Error fetching user:', err);
      setError(err.message || 'An error occurred while searching for the user');
      clearMessages();
    } finally {
      setLoading(false);
    }
  };

  const handleAdminStatusChange = async (isAdmin) => {
    if (!userData) return;
    
    setLoading(true);
    setLocalError(null);
    setLocalSuccess(null);
    
    try {
      const token = await currentUser.getIdToken();
      
      const response = await fetch('/api/auth/setAdmin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          uid: userData.uid,
          isAdmin
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update admin status');
      }
      
      // Update local state
      setUserClaims(prev => ({
        ...prev,
        isAdmin
      }));
      
      setSuccess(`Successfully updated admin status to ${isAdmin}`);
      clearMessages();
    } catch (err) {
      console.error('Error updating admin status:', err);
      setError(err.message || 'Failed to update admin status');
      clearMessages();
    } finally {
      setLoading(false);
    }
  };

  const handleTierStatusChange = async (tier, isEnabled) => {
    if (!userData) return;
    
    setLoading(true);
    setLocalError(null);
    setLocalSuccess(null);
    
    try {
      const token = await currentUser.getIdToken();
      
      const response = await fetch(`/api/auth/setTier${tier}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          uid: userData.uid,
          isEnabled
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to update Tier ${tier} status`);
      }
      
      // Update local state
      setUserClaims(prev => ({
        ...prev,
        [`isTier${tier}User`]: isEnabled
      }));
      
      setSuccess(`Successfully updated Tier ${tier} status to ${isEnabled}`);
      clearMessages();
    } catch (err) {
      console.error(`Error updating Tier ${tier} status:`, err);
      setError(err.message || `Failed to update Tier ${tier} status`);
      clearMessages();
    } finally {
      setLoading(false);
    }
  };

  // Refresh user claims after any update
  const refreshUserClaims = async () => {
    if (!userData) return;
    
    try {
      const token = await currentUser.getIdToken();
      
      const claimsResponse = await fetch('/api/auth/getUserClaims', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ uid: userData.uid })
      });
      
      if (!claimsResponse.ok) {
        throw new Error('Failed to refresh user claims');
      }
      
      const claimsData = await claimsResponse.json();
      setUserClaims(claimsData.customClaims || {});
      
    } catch (err) {
      console.error('Error refreshing claims:', err);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          User Claim Management
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {localError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
            {localError}
          </Alert>
        )}

        {localSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLocalSuccess(null)}>
            {localSuccess}
          </Alert>
        )}
        
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={12}>
            <ToggleButtonGroup
              value={searchType}
              exclusive
              onChange={handleSearchTypeChange}
              aria-label="search type"
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="uid" aria-label="search by uid">
                <FingerprintIcon sx={{ mr: 1 }} /> User ID
              </ToggleButton>
              <ToggleButton value="email" aria-label="search by email">
                <EmailIcon sx={{ mr: 1 }} /> Email
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              label={searchType === 'uid' ? "Enter User ID (UID)" : "Enter User Email"}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder={searchType === 'uid' ? "Firebase User ID" : "user@example.com"}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button 
              variant="contained" 
              onClick={fetchUser} 
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              Find User
            </Button>
          </Grid>
        </Grid>

        {userData && userClaims && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                User: {userData.email || userData.uid}
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>User Information</Typography>
                  <Box sx={{ pl: 2, mb: 3 }}>
                    <Typography variant="body2">
                      <strong>User ID:</strong> {userData.uid}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong> {userData.email || 'Not set'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Display Name:</strong> {userData.displayName || 'Not set'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email Verified:</strong> {userData.emailVerified ? 'Yes' : 'No'}
                    </Typography>
                    {userData.metadata && (
                      <>
                        <Typography variant="body2">
                          <strong>Created:</strong> {userData.metadata.creationTime ? new Date(userData.metadata.creationTime).toLocaleString() : 'Unknown'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Last Sign In:</strong> {userData.metadata.lastSignInTime ? new Date(userData.metadata.lastSignInTime).toLocaleString() : 'Never'}
                        </Typography>
                      </>
                    )}
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    onClick={refreshUserClaims}
                    disabled={loading}
                    size="small"
                  >
                    Refresh Claims
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Manage Claims</Typography>
                  <Box sx={{ pl: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!userClaims.isAdmin}
                          onChange={(e) => handleAdminStatusChange(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Admin"
                    />
                    
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Tier Access:</Typography>
                    
                    <Grid container spacing={1}>
                      {[1, 2, 3, 4, 5].map(tier => (
                        <Grid item xs={6} key={tier}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!userClaims[`isTier${tier}User`]}
                                onChange={(e) => handleTierStatusChange(tier, e.target.checked)}
                                color="primary"
                              />
                            }
                            label={`Tier ${tier}`}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>All Current Claims</Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ pl: 2, backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                    <pre style={{ margin: 0, overflow: 'auto' }}>
                      {JSON.stringify(userClaims, null, 2)}
                    </pre>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {!userData && !loading && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Enter a user ID or email to manage their claims.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default ClaimAdmin;
